import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { ConfidenceBrowser } from '../components/ConfidenceBrowser';
import { 
  Shield, 
  Users, 
  Settings as SettingsIcon, 
  RefreshCw, 
  Upload,
  DollarSign,
  Bell,
  Key,
  Database,
  Save,
  Trash2,
  Check,
  X,
  Loader2,
  Target,
  CheckCircle2,
  XCircle,
  History,
  TrendingUp,
  FileText,
  Share2,
  Twitter,
  Instagram,
  Facebook,
  Sparkles,
  Image,
  Copy,
  Edit,
  RotateCcw,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  const [featureFlags, setFeatureFlags] = useState([]);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [accuracy, setAccuracy] = useState(null);
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [predictionSummary, setPredictionSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Content Management State
  const [content, setContent] = useState([]);
  const [editingContent, setEditingContent] = useState({});
  const [savingContent, setSavingContent] = useState(false);
  
  // Social Media State
  const [socialPosts, setSocialPosts] = useState([]);
  const [generatingPost, setGeneratingPost] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [newPost, setNewPost] = useState({
    platform: 'twitter',
    content_type: 'post',
    topic: '',
    custom_prompt: ''
  });

  // Notifications State
  const [pushStats, setPushStats] = useState({ total: 0, active: 0 });
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [newNotification, setNewNotification] = useState({
    title: '',
    body: '',
    url: '',
    target: 'all'
  });

  // CSV/JSON Upload State
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [predictionStatus, setPredictionStatus] = useState(null);
  const [generatingPredictions, setGeneratingPredictions] = useState(false);

  // Integration Status State
  const [integrationStatus, setIntegrationStatus] = useState(null);
  
  // 2FA Security State
  const [twoFAStatus, setTwoFAStatus] = useState({ available: false, totp_enabled: false });
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAPassword, setTwoFAPassword] = useState('');
  const [enabling2FA, setEnabling2FA] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [setting2FA, setSetting2FA] = useState(false);
  
  // Version Tracking State
  const [versionInfo, setVersionInfo] = useState(null);
  const [checkingVersion, setCheckingVersion] = useState(false);
  const [productionUrl, setProductionUrl] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/board');
      return;
    }
    // PERFORMANCE: Run all initial fetches in parallel
    const initializeAdmin = async () => {
      await Promise.all([
        fetchData(),
        fetchIntegrationStatus(),
        fetch2FAStatus(),
        fetchVersionInfo()
      ]);
    };
    initializeAdmin();
  }, [isAdmin, navigate]);
  
  const fetchVersionInfo = async (prodUrl = null) => {
    setCheckingVersion(true);
    const token = localStorage.getItem('token');
    try {
      let url = `${API_URL}/api/admin/version-check`;
      if (prodUrl) {
        url += `?production_url=${encodeURIComponent(prodUrl)}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVersionInfo(data);
      }
    } catch (err) {
      console.error('Failed to fetch version info:', err);
    } finally {
      setCheckingVersion(false);
    }
  };

  const fetch2FAStatus = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTwoFAStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    }
  };

  const setup2FA = async () => {
    setSetting2FA(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTwoFASetup(data);
        toast.success('Scan the QR code with your authenticator app');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to setup 2FA');
      }
    } catch (err) {
      toast.error('Failed to setup 2FA');
    } finally {
      setSetting2FA(false);
    }
  };

  const enable2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Enter a valid 6-digit code');
      return;
    }
    setEnabling2FA(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/enable`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: twoFACode }),
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('2FA has been enabled!');
        setTwoFASetup(null);
        setTwoFACode('');
        fetch2FAStatus();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Invalid code');
      }
    } catch (err) {
      toast.error('Failed to enable 2FA');
    } finally {
      setEnabling2FA(false);
    }
  };

  const disable2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Enter your current authenticator code');
      return;
    }
    if (!twoFAPassword) {
      toast.error('Enter your account password');
      return;
    }
    setDisabling2FA(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: twoFACode, password: twoFAPassword }),
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('2FA has been disabled');
        setTwoFACode('');
        setTwoFAPassword('');
        fetch2FAStatus();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to disable 2FA');
      }
    } catch (err) {
      toast.error('Failed to disable 2FA');
    } finally {
      setDisabling2FA(false);
    }
  };

  const fetchIntegrationStatus = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/admin/integration-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIntegrationStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch integration status:', err);
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    
    try {
      // PERFORMANCE: All 10 API calls run in parallel
      const [flagsRes, usersRes, accuracyRes, refreshRes, predRes, contentRes, socialRes, pushStatsRes, notifHistRes, predStatusRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/feature-flags`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/admin/users?limit=20`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/accuracy/detailed`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/admin/refresh-status`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/predictions/admin/summary`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/admin/content`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/admin/social/posts?limit=20`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/admin/push-subscriptions/stats`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/admin/notifications/history?limit=20`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/admin/prediction-status`, { credentials: 'include', headers })
      ]);

      // Process all responses (these are fast - just JSON parsing)
      if (flagsRes.ok) setFeatureFlags(await flagsRes.json());
      if (usersRes.ok) {
        const userData = await usersRes.json();
        setUsers(userData.users || []);
        setTotalUsers(userData.total || 0);
      }
      if (accuracyRes.ok) setAccuracy(await accuracyRes.json());
      if (refreshRes.ok) setRefreshStatus(await refreshRes.json());
      if (predRes.ok) setPredictionSummary(await predRes.json());
      if (contentRes.ok) {
        const contentData = await contentRes.json();
        setContent(contentData.content || []);
      }
      if (socialRes.ok) {
        const socialData = await socialRes.json();
        setSocialPosts(socialData.posts || []);
      }
      if (pushStatsRes.ok) {
        const pushData = await pushStatsRes.json();
        setPushStats({ total: pushData.total_subscriptions, active: pushData.active_subscriptions });
      }
      if (notifHistRes.ok) {
        const notifData = await notifHistRes.json();
        setNotificationHistory(notifData.notifications || []);
      }
      if (predStatusRes.ok) setPredictionStatus(await predStatusRes.json());
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Content Management Functions
  const updateContent = async (key, value) => {
    setSavingContent(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/content`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ key, value })
      });

      if (response.ok) {
        setContent(prev => prev.map(c => c.key === key ? { ...c, value, is_default: false } : c));
        setEditingContent(prev => ({ ...prev, [key]: undefined }));
        toast.success(`Content "${key}" updated`);
      } else {
        throw new Error('Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update content');
    } finally {
      setSavingContent(false);
    }
  };

  const resetContent = async (key) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/content/${key}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setContent(prev => prev.map(c => c.key === key ? { ...c, value: data.default_value, is_default: true } : c));
        setEditingContent(prev => ({ ...prev, [key]: undefined }));
        toast.success(`Content "${key}" reset to default`);
      }
    } catch (err) {
      toast.error('Failed to reset content');
    }
  };

  // Social Media Functions
  const generateSocialPost = async () => {
    setGeneratingPost(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/social/generate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(newPost)
      });

      if (response.ok) {
        const data = await response.json();
        setSocialPosts(prev => [data, ...prev]);
        toast.success('Post generated!');
        setNewPost({ platform: 'twitter', content_type: 'post', topic: '', custom_prompt: '' });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to generate post');
    } finally {
      setGeneratingPost(false);
    }
  };

  const generateImage = async (postId) => {
    setGeneratingImage(postId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/social/generate-image`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ post_id: postId })
      });

      if (response.ok) {
        const data = await response.json();
        setSocialPosts(prev => prev.map(p => p.post_id === postId ? { ...p, image_url: data.image_url } : p));
        toast.success('Image generated!');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate image');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const deleteSocialPost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/social/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        setSocialPosts(prev => prev.filter(p => p.post_id !== postId));
        toast.success('Post deleted');
      }
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // CSV/JSON Upload Functions
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['text/csv', 'application/json', 'text/plain'];
      const validExtensions = ['.csv', '.json'];
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (!validTypes.includes(file.type) && !hasValidExtension) {
        toast.error('Please upload a CSV or JSON file');
        return;
      }
      setUploadFile(file);
      setUploadResult(null);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await fetch(`${API_URL}/api/admin/upload-board`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setUploadResult(result);
        toast.success(`Successfully uploaded ${result.games_created || 0} games`);
        setUploadFile(null);
        fetchData(); // Refresh data
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to upload file');
      setUploadResult({ error: err.message });
    } finally {
      setUploading(false);
    }
  };

  const generateAllPredictions = async (useUnified = true) => {
    setGeneratingPredictions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/generate-all-predictions?use_unified=${useUnified}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Generated predictions: ${result.success_count} success, ${result.error_count} errors`);
        // Refresh prediction status
        const statusRes = await fetch(`${API_URL}/api/admin/prediction-status`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statusRes.ok) {
          setPredictionStatus(await statusRes.json());
        }
      } else {
        throw new Error('Failed to generate predictions');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to generate predictions');
    } finally {
      setGeneratingPredictions(false);
    }
  };

  const triggerPregameUpdates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/trigger-pregame-updates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Pre-game prediction updates triggered');
        // Refresh status
        const statusRes = await fetch(`${API_URL}/api/admin/prediction-status`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statusRes.ok) {
          setPredictionStatus(await statusRes.json());
        }
      }
    } catch (err) {
      toast.error('Failed to trigger updates');
    }
  };

  // Notification Functions
  const sendNotification = async () => {
    if (!newNotification.title || !newNotification.body) {
      toast.error('Title and body are required');
      return;
    }
    
    setSendingNotification(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/notifications/send`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(newNotification)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Notification sent to ${data.sent_count} users`);
        setNewNotification({ title: '', body: '', url: '', target: 'all' });
        // Refresh history
        const histRes = await fetch(`${API_URL}/api/admin/notifications/history?limit=20`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (histRes.ok) {
          const histData = await histRes.json();
          setNotificationHistory(histData.notifications || []);
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to send notification');
    } finally {
      setSendingNotification(false);
    }
  };

  const updateFeatureFlag = async (key, value) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/feature-flags`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ key, value })
      });

      if (response.ok) {
        setFeatureFlags(prev => 
          prev.map(f => f.key === key ? { ...f, value } : f)
        );
        toast.success(`Setting "${key}" updated successfully`);
        
        // Refresh integration status if API key or sender email was updated
        if (['stripe_api_key', 'resend_api_key', 'sender_email'].includes(key)) {
          fetchIntegrationStatus();
        }
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const toggleUserAdmin = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/toggle-admin`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setUsers(prev => 
          prev.map(u => u.user_id === userId ? { ...u, is_admin: !u.is_admin } : u)
        );
        toast.success('User admin status updated');
      }
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const toggleFreeAccount = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/toggle-free-account`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(prev => 
          prev.map(u => u.user_id === userId ? { 
            ...u, 
            has_free_account: data.has_free_account,
            is_subscriber: data.is_subscriber 
          } : u)
        );
        toast.success(data.message);
      }
    } catch (err) {
      toast.error('Failed to update free account status');
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/refresh-games`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Refresh complete! ${data.total_games} games, ${data.games_today} today`);
        // Refresh status
        const refreshRes = await fetch(`${API_URL}/api/admin/refresh-status`, { credentials: 'include' });
        if (refreshRes.ok) {
          setRefreshStatus(await refreshRes.json());
        }
      }
    } catch (err) {
      toast.error('Failed to trigger refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const seedData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/seed`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      }
    } catch (err) {
      toast.error('Failed to seed data');
    }
  };

  const billingEnabled = featureFlags.find(f => f.key === 'billing_enabled')?.value || false;
  const alertsEnabled = featureFlags.find(f => f.key === 'alerts_enabled')?.value || false;
  const resendApiKey = featureFlags.find(f => f.key === 'resend_api_key')?.value || '';

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        <div className="border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-red-400" />
              <div>
                <h1 className="font-display text-3xl font-bold uppercase tracking-tight">
                  Admin Panel
                </h1>
                <p className="text-muted-foreground">Manage settings, users, and feature flags</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Tabs defaultValue="accuracy" className="w-full">
            <TabsList className="rounded-sm">
              <TabsTrigger value="accuracy" className="rounded-sm" data-testid="accuracy-tab">
                <Target className="w-4 h-4 mr-2" />
                Accuracy
              </TabsTrigger>
              <TabsTrigger value="predictions" className="rounded-sm" data-testid="predictions-tab">
                <History className="w-4 h-4 mr-2" />
                User Predictions
              </TabsTrigger>
              <TabsTrigger value="flags" className="rounded-sm" data-testid="flags-tab">
                <SettingsIcon className="w-4 h-4 mr-2" />
                Feature Flags
              </TabsTrigger>
              <TabsTrigger value="content" className="rounded-sm" data-testid="content-tab">
                <FileText className="w-4 h-4 mr-2" />
                Content
              </TabsTrigger>
              <TabsTrigger value="social" className="rounded-sm" data-testid="social-tab">
                <Share2 className="w-4 h-4 mr-2" />
                Social Media
              </TabsTrigger>
              <TabsTrigger value="data" className="rounded-sm" data-testid="data-tab">
                <Database className="w-4 h-4 mr-2" />
                Data Management
              </TabsTrigger>
              <TabsTrigger value="notifications" className="rounded-sm" data-testid="notifications-tab">
                <Bell className="w-4 h-4 mr-2" />
                Notifications
              </TabsTrigger>
              <TabsTrigger value="apis" className="rounded-sm" data-testid="apis-tab">
                <Key className="w-4 h-4 mr-2" />
                Platform APIs
              </TabsTrigger>
              <TabsTrigger value="security" className="rounded-sm" data-testid="security-tab">
                <Shield className="w-4 h-4 mr-2" />
                Security
              </TabsTrigger>
            </TabsList>

            {/* Accuracy Stats */}
            <TabsContent value="accuracy" className="mt-6">
              <div className="space-y-6">
                {/* Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-card border border-border rounded-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Target className="w-6 h-6 text-red-400" />
                      <h3 className="font-display font-bold uppercase">Overall Accuracy</h3>
                    </div>
                    <p className="font-mono text-4xl font-bold text-green-400">
                      {Number(accuracy?.accuracy_percentage || 0).toFixed(1)}%
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {accuracy?.correct_predictions || 0} / {accuracy?.total_predictions || 0} correct
                    </p>
                  </div>

                  <div className="bg-card border border-border rounded-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <CheckCircle2 className="w-6 h-6 text-green-400" />
                      <h3 className="font-display font-bold uppercase">Hot Streak</h3>
                    </div>
                    <p className="font-mono text-4xl font-bold text-orange-400">
                      {accuracy?.recent_streak || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">consecutive correct</p>
                  </div>

                  <div className="bg-card border border-border rounded-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Database className="w-6 h-6 text-blue-400" />
                      <h3 className="font-display font-bold uppercase">Total Predictions</h3>
                    </div>
                    <p className="font-mono text-4xl font-bold">
                      {accuracy?.total_predictions || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">games analyzed</p>
                  </div>
                </div>

                {/* By League */}
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                    Accuracy by League
                  </h3>
                  {accuracy?.by_league && Object.keys(accuracy.by_league).length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(accuracy.by_league).map(([league, data]) => (
                        <div key={league} className="bg-secondary/30 rounded-sm p-3">
                          <p className="font-display font-bold uppercase text-sm">{league.toUpperCase()}</p>
                          <p className="font-mono text-2xl font-bold mt-1">
                            <span className={Number(data.accuracy || 0) >= 55 ? 'text-green-400' : 'text-yellow-400'}>
                              {Number(data.accuracy || 0).toFixed(1)}%
                            </span>
                          </p>
                          <p className="text-xs text-muted-foreground">{data.correct}/{data.total}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No league data yet</p>
                  )}
                </div>

                {/* By Confidence */}
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                    Accuracy by Confidence Level
                  </h3>
                  {accuracy?.by_confidence && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {['High', 'Medium', 'Low'].map((level) => {
                        const data = accuracy.by_confidence[level] || { total: 0, correct: 0, accuracy: 0 };
                        return (
                          <div key={level} className="text-center">
                            <Badge className={`mb-2 ${
                              level === 'High' ? 'confidence-high' :
                              level === 'Medium' ? 'confidence-medium' : 'confidence-low'
                            }`}>
                              {level}
                            </Badge>
                            <p className="font-mono text-3xl font-bold">
                              <span className={Number(data.accuracy || 0) >= 55 ? 'text-green-400' : 'text-yellow-400'}>
                                {Number(data.accuracy || 0).toFixed(1)}%
                              </span>
                            </p>
                            <p className="text-sm text-muted-foreground">{data.correct}/{data.total}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Recent Predictions */}
                {accuracy?.recent_predictions && accuracy.recent_predictions.length > 0 && (
                  <div className="bg-card border border-border rounded-sm p-6">
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                      Recent Predictions
                    </h3>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {accuracy.recent_predictions.slice(0, 20).map((pred, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center justify-between p-2 bg-secondary/30 rounded-sm hover:bg-secondary/50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/game/${pred.game_id}`)}
                        >
                          <div className="flex items-center gap-3">
                            {pred.is_correct ? (
                              <CheckCircle2 className="w-4 h-4 text-green-400" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400" />
                            )}
                            <div>
                              <p className="font-medium text-sm">
                                {pred.away_team || 'Away'} @ {pred.home_team || 'Home'}
                              </p>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{pred.league_id?.toUpperCase()}</Badge>
                                <span className="text-xs text-muted-foreground">
                                  Picked: {pred.predicted_winner === 'home' ? pred.home_team : pred.away_team}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            {pred.home_score != null && (
                              <span className="font-mono text-sm">
                                {pred.away_score} - {pred.home_score}
                              </span>
                            )}
                            <Badge 
                              variant={pred.is_correct ? "success" : "destructive"}
                              className={`ml-2 ${pred.is_correct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
                            >
                              {pred.is_correct ? 'CORRECT' : 'INCORRECT'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Confidence Browser Section */}
                <div className="mt-8">
                  <ConfidenceBrowser isAdmin={true} />
                </div>
              </div>
            </TabsContent>

            {/* User Predictions Tab */}
            <TabsContent value="predictions" className="mt-6">
              {predictionSummary ? (
                <div className="space-y-6">
                  {/* Overview Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-card border border-border rounded-sm p-6 text-center">
                      <p className="font-mono text-3xl font-bold">{predictionSummary.user_predictions?.total || 0}</p>
                      <p className="text-sm text-muted-foreground">Total User Predictions</p>
                    </div>
                    <div className="bg-card border border-border rounded-sm p-6 text-center">
                      <p className="font-mono text-3xl font-bold text-green-400">{predictionSummary.user_predictions?.correct || 0}</p>
                      <p className="text-sm text-muted-foreground">Correct</p>
                    </div>
                    <div className="bg-card border border-border rounded-sm p-6 text-center">
                      <p className={`font-mono text-3xl font-bold ${
                        predictionSummary.user_predictions?.accuracy >= 60 ? 'text-green-400' : 
                        predictionSummary.user_predictions?.accuracy >= 50 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {predictionSummary.user_predictions?.accuracy || 0}%
                      </p>
                      <p className="text-sm text-muted-foreground">User Accuracy</p>
                    </div>
                    <div className="bg-card border border-border rounded-sm p-6 text-center">
                      <p className="font-mono text-3xl font-bold text-blue-400">{predictionSummary.active_predictors || 0}</p>
                      <p className="text-sm text-muted-foreground">Active Predictors (7d)</p>
                    </div>
                  </div>

                  {/* Platform vs User Accuracy Comparison */}
                  <div className="bg-card border border-border rounded-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      <h3 className="font-display font-bold uppercase">Platform vs User Accuracy</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="text-center p-4 bg-secondary/30 rounded-sm">
                        <p className="text-sm text-muted-foreground mb-2">Platform Accuracy</p>
                        <p className="font-mono text-4xl font-bold text-green-400">
                          {Number(predictionSummary.platform_accuracy?.accuracy_percentage || 0).toFixed(1)}%
                        </p>
                      </div>
                      <div className="text-center p-4 bg-secondary/30 rounded-sm">
                        <p className="text-sm text-muted-foreground mb-2">Avg User Accuracy</p>
                        <p className="font-mono text-4xl font-bold text-blue-400">
                          {predictionSummary.user_predictions?.accuracy || 0}%
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* By League */}
                  {predictionSummary.by_league && Object.keys(predictionSummary.by_league).length > 0 && (
                    <div className="bg-card border border-border rounded-sm p-6">
                      <h3 className="font-display font-bold uppercase mb-4">User Predictions by League</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(predictionSummary.by_league).map(([league, data]) => (
                          <div key={league} className="text-center p-4 bg-secondary/30 rounded-sm">
                            <p className="font-bold uppercase mb-1">{league}</p>
                            <p className={`font-mono text-2xl font-bold ${
                              data.accuracy >= 60 ? 'text-green-400' : 
                              data.accuracy >= 50 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {data.accuracy}%
                            </p>
                            <p className="text-xs text-muted-foreground">{data.correct}/{data.total}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Predictions */}
                  {predictionSummary.recent_predictions?.length > 0 && (
                    <div className="bg-card border border-border rounded-sm p-6">
                      <h3 className="font-display font-bold uppercase mb-4">Recent User Predictions</h3>
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {predictionSummary.recent_predictions.map((pred, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm"
                          >
                            <div className="flex items-center gap-3">
                              {pred.status === 'pending' ? (
                                <div className="w-6 h-6 bg-yellow-500/20 rounded-full flex items-center justify-center">
                                  <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                                </div>
                              ) : pred.is_correct ? (
                                <CheckCircle2 className="w-5 h-5 text-green-400" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-400" />
                              )}
                              <div>
                                <p className="text-sm font-medium">
                                  {pred.away_team_name} @ {pred.home_team_name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Picked: {pred.predicted_winner === 'home' ? pred.home_team_name : pred.away_team_name}
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {pred.league_id?.toUpperCase()}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No user prediction data available</p>
                </div>
              )}
            </TabsContent>

            {/* Feature Flags */}
            <TabsContent value="flags" className="mt-6">
              <div className="space-y-6">
                {/* Billing Toggle */}
                <div className="bg-card border border-border rounded-sm p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                          Billing System
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Enable $20 CAD/month subscription for premium features
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={billingEnabled ? 'default' : 'secondary'}>
                        {billingEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                      <Switch
                        checked={billingEnabled}
                        onCheckedChange={(checked) => updateFeatureFlag('billing_enabled', checked)}
                        disabled={saving}
                        data-testid="billing-toggle"
                      />
                    </div>
                  </div>
                </div>

                {/* Alerts Toggle */}
                <div className="bg-card border border-border rounded-sm p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center">
                        <Bell className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                          Alert System
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Enable user alerts for notable game matchups
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={alertsEnabled ? 'default' : 'secondary'}>
                        {alertsEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                      <Switch
                        checked={alertsEnabled}
                        onCheckedChange={(checked) => updateFeatureFlag('alerts_enabled', checked)}
                        disabled={saving}
                        data-testid="alerts-toggle"
                      />
                    </div>
                  </div>
                </div>

                {/* Resend API Key */}
                <div className="bg-card border border-border rounded-sm p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-sm flex items-center justify-center">
                      <Key className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                        Resend API Key
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        API key for sending emails (verification, alerts, etc.)
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Input
                      type="password"
                      placeholder="re_..."
                      value={resendApiKey}
                      onChange={(e) => {
                        setFeatureFlags(prev => 
                          prev.map(f => f.key === 'resend_api_key' ? { ...f, value: e.target.value } : f)
                        );
                      }}
                      className="rounded-sm"
                      data-testid="resend-api-key-input"
                    />
                    <Button
                      onClick={() => updateFeatureFlag('resend_api_key', resendApiKey)}
                      disabled={saving}
                      className="rounded-sm"
                      data-testid="save-resend-key-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>


            {/* Data Management */}
            <TabsContent value="data" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ESPN Refresh Status */}
                <div className="bg-card border border-border rounded-sm p-6 md:col-span-2">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
                        <RefreshCw className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                          ESPN Data Status
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Auto-refresh every {refreshStatus?.refresh_interval || '6 hours'}
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={refreshData} 
                      className="rounded-sm"
                      disabled={refreshing}
                      data-testid="refresh-espn-btn"
                    >
                      {refreshing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {refreshing ? 'Refreshing...' : 'Refresh Now'}
                    </Button>
                  </div>
                  
                  {refreshStatus && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-secondary/30 rounded-sm p-4 text-center">
                        <p className="text-2xl font-mono font-bold">{refreshStatus.total_games || 0}</p>
                        <p className="text-xs text-muted-foreground">Total Games</p>
                      </div>
                      <div className="bg-secondary/30 rounded-sm p-4 text-center">
                        <p className="text-2xl font-mono font-bold">{refreshStatus.total_teams || 0}</p>
                        <p className="text-xs text-muted-foreground">Total Teams</p>
                      </div>
                      <div className="bg-secondary/30 rounded-sm p-4 text-center">
                        <p className="text-2xl font-mono font-bold text-green-400">
                          {refreshStatus.last_refresh ? new Date(refreshStatus.last_refresh).toLocaleTimeString() : 'Never'}
                        </p>
                        <p className="text-xs text-muted-foreground">Last Refresh</p>
                      </div>
                      <div className="bg-secondary/30 rounded-sm p-4 text-center">
                        <p className="text-2xl font-mono font-bold text-blue-400">6h</p>
                        <p className="text-xs text-muted-foreground">Refresh Interval</p>
                      </div>
                    </div>
                  )}

                  {/* Games by League */}
                  {refreshStatus?.games_by_league && (
                    <div className="mb-6">
                      <h4 className="font-display text-sm font-bold uppercase tracking-tight mb-3">Games by League</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                        {Object.entries(refreshStatus.games_by_league).map(([league, count]) => (
                          <div key={league} className="bg-secondary/20 rounded-sm p-2 text-center">
                            <p className="font-mono font-bold">{count}</p>
                            <p className="text-xs text-muted-foreground truncate">{league}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Games by Date */}
                  {refreshStatus?.games_by_date && (
                    <div>
                      <h4 className="font-display text-sm font-bold uppercase tracking-tight mb-3">Games Next 7 Days</h4>
                      <div className="grid grid-cols-7 gap-2">
                        {Object.entries(refreshStatus.games_by_date).map(([date, count]) => (
                          <div key={date} className="bg-secondary/20 rounded-sm p-2 text-center">
                            <p className="font-mono font-bold">{count}</p>
                            <p className="text-xs text-muted-foreground">{date.slice(5)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-card border border-border rounded-sm p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
                      <Database className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                        Seed Sample Data
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Populate database with sample leagues, teams, and games
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={seedData} 
                    className="rounded-sm w-full"
                    data-testid="seed-data-btn"
                  >
                    <Database className="w-4 h-4 mr-2" />
                    Seed Data
                  </Button>
                </div>

                <div className="bg-card border border-border rounded-sm p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-sm flex items-center justify-center">
                      <Upload className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                        Manual Board Upload
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Upload games manually via CSV or JSON (admin only)
                      </p>
                    </div>
                  </div>
                  <div className="border-2 border-dashed border-border rounded-sm p-8 text-center">
                    <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-3">
                      Drag and drop CSV/JSON file or click to browse
                    </p>
                    <Button variant="outline" className="rounded-sm">
                      Choose File
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Content Management */}
            <TabsContent value="content" className="mt-6">
              <div className="space-y-6">
                <div className="bg-card border border-border rounded-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                        Content Management System
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Edit all user-facing text on the website
                      </p>
                    </div>
                  </div>
                  
                  {content.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">Loading content...</p>
                  ) : (
                    <div className="space-y-4">
                      {content.map((item) => (
                        <div 
                          key={item.key} 
                          className="bg-secondary/20 rounded-sm p-4"
                          data-testid={`content-item-${item.key}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Label className="font-mono text-sm">{item.key}</Label>
                              {item.is_default && (
                                <Badge variant="outline" className="text-xs">Default</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {editingContent[item.key] !== undefined ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingContent(prev => ({ ...prev, [item.key]: undefined }))}
                                    className="rounded-sm"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => updateContent(item.key, editingContent[item.key])}
                                    disabled={savingContent}
                                    className="rounded-sm"
                                  >
                                    {savingContent ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Check className="w-4 h-4" />
                                    )}
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingContent(prev => ({ ...prev, [item.key]: item.value }))}
                                    className="rounded-sm"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  {!item.is_default && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => resetContent(item.key)}
                                      className="rounded-sm text-orange-400 hover:text-orange-300"
                                    >
                                      <RotateCcw className="w-4 h-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          
                          {editingContent[item.key] !== undefined ? (
                            <Textarea
                              value={editingContent[item.key]}
                              onChange={(e) => setEditingContent(prev => ({ ...prev, [item.key]: e.target.value }))}
                              className="rounded-sm min-h-[80px] font-mono text-sm"
                              data-testid={`content-edit-${item.key}`}
                            />
                          ) : (
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {item.value}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Social Media Management */}
            <TabsContent value="social" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Generate New Post */}
                <div className="lg:col-span-1">
                  <div className="bg-card border border-border rounded-sm p-6 sticky top-4">
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                      Generate Content
                    </h3>
                    
                    <div className="space-y-4">
                      {/* Platform Select */}
                      <div className="space-y-2">
                        <Label>Platform</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { id: 'twitter', icon: Twitter, label: 'X/Twitter' },
                            { id: 'instagram', icon: Instagram, label: 'Instagram' },
                            { id: 'facebook', icon: Facebook, label: 'Facebook' },
                            { id: 'tiktok', icon: Share2, label: 'TikTok' }
                          ].map((platform) => (
                            <Button
                              key={platform.id}
                              variant={newPost.platform === platform.id ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setNewPost(prev => ({ ...prev, platform: platform.id }))}
                              className="rounded-sm justify-start"
                              data-testid={`platform-${platform.id}`}
                            >
                              <platform.icon className="w-4 h-4 mr-2" />
                              {platform.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Content Type */}
                      <div className="space-y-2">
                        <Label>Content Type</Label>
                        <div className="flex gap-2">
                          {['post', 'reel', 'story'].map((type) => (
                            <Button
                              key={type}
                              variant={newPost.content_type === type ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setNewPost(prev => ({ ...prev, content_type: type }))}
                              className="rounded-sm capitalize flex-1"
                              data-testid={`type-${type}`}
                            >
                              {type}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Topic */}
                      <div className="space-y-2">
                        <Label htmlFor="topic">Topic/Theme</Label>
                        <Input
                          id="topic"
                          placeholder="e.g., NBA playoffs, game night promo..."
                          value={newPost.topic}
                          onChange={(e) => setNewPost(prev => ({ ...prev, topic: e.target.value }))}
                          className="rounded-sm"
                          data-testid="social-topic-input"
                        />
                      </div>
                      
                      {/* Custom Prompt */}
                      <div className="space-y-2">
                        <Label htmlFor="custom-prompt">Custom Instructions (Optional)</Label>
                        <Textarea
                          id="custom-prompt"
                          placeholder="Any specific instructions for the AI..."
                          value={newPost.custom_prompt}
                          onChange={(e) => setNewPost(prev => ({ ...prev, custom_prompt: e.target.value }))}
                          className="rounded-sm min-h-[80px]"
                          data-testid="social-prompt-input"
                        />
                      </div>
                      
                      {/* Generate Button */}
                      <Button
                        onClick={generateSocialPost}
                        disabled={generatingPost}
                        className="w-full rounded-sm btn-blaze"
                        data-testid="generate-post-btn"
                      >
                        {generatingPost ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate Post
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Generated Posts List */}
                <div className="lg:col-span-2">
                  <div className="bg-card border border-border rounded-sm p-6">
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                      Generated Posts ({socialPosts.length})
                    </h3>
                    
                    {socialPosts.length === 0 ? (
                      <div className="text-center py-12">
                        <Share2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No posts generated yet</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Use the form to generate AI-powered social media content
                        </p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[600px] pr-4">
                        <div className="space-y-4">
                          {socialPosts.map((post) => (
                            <div 
                              key={post.post_id} 
                              className="bg-secondary/20 rounded-sm p-4"
                              data-testid={`social-post-${post.post_id}`}
                            >
                              {/* Post Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  {post.platform === 'twitter' && <Twitter className="w-4 h-4 text-blue-400" />}
                                  {post.platform === 'instagram' && <Instagram className="w-4 h-4 text-pink-400" />}
                                  {post.platform === 'facebook' && <Facebook className="w-4 h-4 text-blue-500" />}
                                  {post.platform === 'tiktok' && <Share2 className="w-4 h-4 text-cyan-400" />}
                                  <Badge variant="outline" className="text-xs capitalize">{post.platform}</Badge>
                                  <Badge variant="outline" className="text-xs capitalize">{post.content_type}</Badge>
                                  {post.status && (
                                    <Badge className={`text-xs ${
                                      post.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                                    }`}>
                                      {post.status}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => copyToClipboard(post.content)}
                                    className="rounded-sm"
                                    data-testid={`copy-post-${post.post_id}`}
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteSocialPost(post.post_id)}
                                    className="rounded-sm text-red-400 hover:text-red-300"
                                    data-testid={`delete-post-${post.post_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Post Content */}
                              <div className="bg-background/50 rounded-sm p-3 mb-3">
                                <p className="text-sm whitespace-pre-wrap">{post.content}</p>
                              </div>
                              
                              {/* Post Footer */}
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>{post.char_count || post.content?.length || 0} characters</span>
                                <span>
                                  {post.created_at && new Date(post.created_at).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Send Notification */}
                <div className="lg:col-span-1">
                  <div className="bg-card border border-border rounded-sm p-6 sticky top-4">
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
                      <Bell className="w-5 h-5 text-yellow-400" />
                      Send Notification
                    </h3>
                    
                    {/* Push Stats */}
                    <div className="bg-secondary/20 rounded-sm p-3 mb-4">
                      <p className="text-sm">
                        <span className="text-muted-foreground">Active Subscribers:</span>{' '}
                        <span className="font-bold text-green-400">{pushStats.active}</span>
                        <span className="text-muted-foreground"> / {pushStats.total} total</span>
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      {/* Target Audience */}
                      <div className="space-y-2">
                        <Label>Target Audience</Label>
                        <div className="flex gap-2">
                          {['all', 'subscribers'].map((target) => (
                            <Button
                              key={target}
                              variant={newNotification.target === target ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setNewNotification(prev => ({ ...prev, target }))}
                              className="rounded-sm capitalize flex-1"
                              data-testid={`target-${target}`}
                            >
                              {target === 'all' ? 'All Users' : 'Subscribers Only'}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Title */}
                      <div className="space-y-2">
                        <Label htmlFor="notif-title">Title</Label>
                        <Input
                          id="notif-title"
                          placeholder="Notification title..."
                          value={newNotification.title}
                          onChange={(e) => setNewNotification(prev => ({ ...prev, title: e.target.value }))}
                          className="rounded-sm"
                          data-testid="notif-title-input"
                        />
                      </div>
                      
                      {/* Body */}
                      <div className="space-y-2">
                        <Label htmlFor="notif-body">Message</Label>
                        <Textarea
                          id="notif-body"
                          placeholder="Notification message..."
                          value={newNotification.body}
                          onChange={(e) => setNewNotification(prev => ({ ...prev, body: e.target.value }))}
                          className="rounded-sm min-h-[80px]"
                          data-testid="notif-body-input"
                        />
                      </div>
                      
                      {/* URL */}
                      <div className="space-y-2">
                        <Label htmlFor="notif-url">Link URL (Optional)</Label>
                        <Input
                          id="notif-url"
                          placeholder="/dashboard or https://..."
                          value={newNotification.url}
                          onChange={(e) => setNewNotification(prev => ({ ...prev, url: e.target.value }))}
                          className="rounded-sm"
                          data-testid="notif-url-input"
                        />
                      </div>
                      
                      {/* Send Button */}
                      <Button
                        onClick={sendNotification}
                        disabled={sendingNotification || pushStats.active === 0}
                        className="w-full rounded-sm btn-blaze"
                        data-testid="send-notification-btn"
                      >
                        {sendingNotification ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Bell className="w-4 h-4 mr-2" />
                            Send Push Notification
                          </>
                        )}
                      </Button>
                      
                      {pushStats.active === 0 && (
                        <p className="text-xs text-yellow-500 text-center">
                          No users have enabled push notifications yet
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Notification History */}
                <div className="lg:col-span-2">
                  <div className="bg-card border border-border rounded-sm p-6">
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                      Notification History ({notificationHistory.length})
                    </h3>
                    
                    {notificationHistory.length === 0 ? (
                      <div className="text-center py-12">
                        <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No notifications sent yet</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[500px] pr-4">
                        <div className="space-y-3">
                          {notificationHistory.map((notif, idx) => (
                            <div 
                              key={notif.notification_id || idx} 
                              className="bg-secondary/20 rounded-sm p-3"
                              data-testid={`notif-history-${idx}`}
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-sm">{notif.title}</p>
                                  <p className="text-xs text-muted-foreground mt-1">{notif.body}</p>
                                </div>
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${
                                    notif.status === 'sent' ? 'text-green-400' : 'text-yellow-400'
                                  }`}
                                >
                                  {notif.status}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                {notif.created_at && new Date(notif.created_at).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Platform APIs */}
            <TabsContent value="apis" className="mt-6">
              <div className="space-y-6">
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="font-semibold mb-1">Platform API Keys</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Manage your integrations for payments, emails, and other services
                  </p>
                  
                  <div className="space-y-6">
                    {/* Stripe API Key */}
                    <div className="space-y-2">
                      <Label htmlFor="stripe-key" className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-400" />
                        Stripe API Key (Payments)
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="stripe-key"
                          type="password"
                          placeholder="sk_live_..."
                          className="rounded-sm font-mono"
                          defaultValue={featureFlags.find(f => f.key === 'stripe_api_key')?.value || ''}
                          onChange={(e) => {
                            // Will save on button click
                          }}
                          data-testid="stripe-key-input"
                        />
                        <Button
                          variant="outline"
                          className="rounded-sm"
                          onClick={async () => {
                            const input = document.getElementById('stripe-key');
                            if (input.value) {
                              await updateFeatureFlag('stripe_api_key', input.value);
                            }
                          }}
                          disabled={saving}
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Used for subscription payments and billing. Get your key from{' '}
                        <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          Stripe Dashboard
                        </a>
                      </p>
                    </div>

                    {/* Resend API Key */}
                    <div className="space-y-2">
                      <Label htmlFor="resend-key" className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-blue-400" />
                        Resend API Key (Emails)
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="resend-key"
                          type="password"
                          placeholder="re_..."
                          className="rounded-sm font-mono"
                          defaultValue={featureFlags.find(f => f.key === 'resend_api_key')?.value || ''}
                          data-testid="resend-key-input"
                        />
                        <Button
                          variant="outline"
                          className="rounded-sm"
                          onClick={async () => {
                            const input = document.getElementById('resend-key');
                            if (input.value) {
                              await updateFeatureFlag('resend_api_key', input.value);
                            }
                          }}
                          disabled={saving}
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Used for verification emails and notifications. Get your key from{' '}
                        <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          Resend Dashboard
                        </a>
                      </p>
                      <p className="text-xs text-orange-400">
                        Note: You must verify a domain at resend.com/domains to send emails to users other than {user?.email}
                      </p>
                    </div>

                    {/* Sender Email */}
                    <div className="space-y-2">
                      <Label htmlFor="sender-email" className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-purple-400" />
                        Sender Email Address
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="sender-email"
                          type="email"
                          placeholder="noreply@yourdomain.com"
                          className="rounded-sm"
                          defaultValue={featureFlags.find(f => f.key === 'sender_email')?.value || ''}
                          data-testid="sender-email-input"
                        />
                        <Button
                          variant="outline"
                          className="rounded-sm"
                          onClick={async () => {
                            const input = document.getElementById('sender-email');
                            if (input.value) {
                              await updateFeatureFlag('sender_email', input.value);
                            }
                          }}
                          disabled={saving}
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        The email address shown as sender for all platform emails
                      </p>
                    </div>
                  </div>
                </div>

                {/* API Status */}
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="font-semibold mb-4">Integration Status</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-border">
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        <span>Stripe (Payments)</span>
                      </div>
                      {integrationStatus?.stripe?.configured ? (
                        <Badge className="bg-green-500/20 text-green-400">
                          {integrationStatus.stripe.type === 'live' ? 'Live' : 'Test'} Connected
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-500/20 text-yellow-400">Not Configured</Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-border">
                      <div className="flex items-center gap-3">
                        <Bell className="w-5 h-5 text-blue-400" />
                        <span>Resend (Emails)</span>
                      </div>
                      {integrationStatus?.resend?.configured ? (
                        <Badge className="bg-green-500/20 text-green-400">Connected</Badge>
                      ) : (
                        <Badge className="bg-yellow-500/20 text-yellow-400">Not Configured</Badge>
                      )}
                    </div>
                    {integrationStatus?.sender_email && (
                      <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-3">
                          <Mail className="w-5 h-5 text-purple-400" />
                          <span>Sender Email</span>
                        </div>
                        <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                          {integrationStatus.sender_email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* API Version Tracking */}
                <div className="bg-card border border-border rounded-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="font-semibold mb-1 flex items-center gap-2">
                        <Database className="w-5 h-5 text-purple-400" />
                        API Version Tracking
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Monitor deployment status and compare preview vs production versions
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="rounded-sm gap-2"
                      onClick={() => fetchVersionInfo(productionUrl || null)}
                      disabled={checkingVersion}
                    >
                      {checkingVersion ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Check Version
                    </Button>
                  </div>
                  
                  {versionInfo && (
                    <div className="space-y-4">
                      {/* Preview Version */}
                      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-blue-500/20 text-blue-400">Preview</Badge>
                            <span className="font-mono font-bold text-lg">{versionInfo.preview?.version}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {versionInfo.preview?.version_date ? new Date(versionInfo.preview.version_date).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{versionInfo.preview?.version_notes}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Server time: {versionInfo.preview?.server_time ? new Date(versionInfo.preview.server_time).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                      
                      {/* Production URL Input */}
                      <div className="space-y-2">
                        <Label className="text-sm">Production URL (for comparison)</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="https://your-production-url.com"
                            value={productionUrl}
                            onChange={(e) => setProductionUrl(e.target.value)}
                            className="rounded-sm font-mono text-sm"
                          />
                          <Button
                            variant="outline"
                            className="rounded-sm"
                            onClick={() => fetchVersionInfo(productionUrl)}
                            disabled={checkingVersion || !productionUrl}
                          >
                            Compare
                          </Button>
                        </div>
                      </div>
                      
                      {/* Production Version (if checked) */}
                      {versionInfo.production && (
                        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-green-500/20 text-green-400">Production</Badge>
                              <span className="font-mono font-bold text-lg">{versionInfo.production.version}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {versionInfo.production.version_date ? new Date(versionInfo.production.version_date).toLocaleString() : 'N/A'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{versionInfo.production.url}</p>
                        </div>
                      )}
                      
                      {/* Deployment Status */}
                      <div className={`p-4 rounded-lg border ${
                        versionInfo.deployment_status?.is_synced === true 
                          ? 'bg-green-500/10 border-green-500/20' 
                          : versionInfo.deployment_status?.is_synced === false 
                          ? 'bg-yellow-500/10 border-yellow-500/20' 
                          : 'bg-secondary/30 border-border'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          {versionInfo.deployment_status?.is_synced === true ? (
                            <>
                              <CheckCircle2 className="w-5 h-5 text-green-400" />
                              <span className="font-medium text-green-400">Deployment Up to Date</span>
                            </>
                          ) : versionInfo.deployment_status?.is_synced === false ? (
                            <>
                              <XCircle className="w-5 h-5 text-yellow-400" />
                              <span className="font-medium text-yellow-400">Deployment Needed</span>
                            </>
                          ) : (
                            <>
                              <History className="w-5 h-5 text-muted-foreground" />
                              <span className="font-medium">Enter production URL to compare</span>
                            </>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{versionInfo.deployment_status?.message}</p>
                        
                        {versionInfo.deployment_status?.version_comparison && (
                          <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-xs text-muted-foreground">Preview Version</p>
                              <p className="font-mono">{versionInfo.deployment_status.version_comparison.preview}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Production Version</p>
                              <p className="font-mono">{versionInfo.deployment_status.version_comparison.production}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Security/2FA Tab */}
            <TabsContent value="security" className="mt-6">
              <div className="space-y-6">
                <div className="bg-card border border-border rounded-sm p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Shield className="w-6 h-6 text-blue-400" />
                    <h3 className="font-display font-bold uppercase">Two-Factor Authentication (2FA)</h3>
                    {twoFAStatus.totp_enabled && (
                      <Badge className="bg-green-500/20 text-green-400">Enabled</Badge>
                    )}
                  </div>

                  {!twoFAStatus.totp_enabled ? (
                    <>
                      <p className="text-muted-foreground mb-6">
                        Add an extra layer of security to your admin account. Once enabled, you'll need to enter 
                        a code from your authenticator app (like Google Authenticator or Authy) when logging in.
                      </p>

                      {!twoFASetup ? (
                        <Button 
                          onClick={setup2FA}
                          disabled={setting2FA}
                          className="btn-blaze rounded-sm"
                          data-testid="setup-2fa-btn"
                        >
                          {setting2FA ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Setting up...
                            </>
                          ) : (
                            <>
                              <Shield className="w-4 h-4 mr-2" />
                              Setup 2FA
                            </>
                          )}
                        </Button>
                      ) : (
                        <div className="space-y-6">
                          <div className="bg-secondary/30 border border-border rounded-sm p-6">
                            <h4 className="font-bold mb-4">Step 1: Scan QR Code</h4>
                            <p className="text-sm text-muted-foreground mb-4">
                              Scan this QR code with your authenticator app:
                            </p>
                            {twoFASetup.qr_code && (
                              <div className="flex justify-center mb-4">
                                <img 
                                  src={twoFASetup.qr_code} 
                                  alt="2FA QR Code" 
                                  className="w-48 h-48 bg-white p-2 rounded-sm"
                                />
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground text-center mb-4">
                              Or enter this secret manually: <code className="bg-secondary px-2 py-1 rounded text-xs">{twoFASetup.secret}</code>
                            </p>
                          </div>

                          <div className="bg-secondary/30 border border-border rounded-sm p-6">
                            <h4 className="font-bold mb-4">Step 2: Verify Code</h4>
                            <p className="text-sm text-muted-foreground mb-4">
                              Enter the 6-digit code from your authenticator app to complete setup:
                            </p>
                            <div className="flex gap-4">
                              <Input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={6}
                                placeholder="000000"
                                value={twoFACode}
                                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                                className="font-mono text-center text-xl tracking-widest rounded-sm max-w-[150px]"
                                data-testid="2fa-verify-input"
                              />
                              <Button 
                                onClick={enable2FA}
                                disabled={enabling2FA || twoFACode.length !== 6}
                                className="btn-blaze rounded-sm"
                                data-testid="enable-2fa-btn"
                              >
                                {enabling2FA ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Enabling...
                                  </>
                                ) : (
                                  <>
                                    <Check className="w-4 h-4 mr-2" />
                                    Enable 2FA
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>

                          <Button 
                            variant="ghost"
                            onClick={() => {
                              setTwoFASetup(null);
                              setTwoFACode('');
                            }}
                          >
                            Cancel Setup
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/20 rounded-sm">
                        <CheckCircle2 className="w-8 h-8 text-green-400" />
                        <div>
                          <p className="font-bold text-green-400">2FA is Active</p>
                          <p className="text-sm text-muted-foreground">
                            Your account is protected with two-factor authentication.
                          </p>
                        </div>
                      </div>

                      <div className="bg-secondary/30 border border-border rounded-sm p-6">
                        <h4 className="font-bold mb-4 text-red-400">Disable 2FA</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          To disable 2FA, enter your current authenticator code and account password:
                        </p>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="disable-code">Authenticator Code</Label>
                            <Input
                              id="disable-code"
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              maxLength={6}
                              placeholder="000000"
                              value={twoFACode}
                              onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                              className="font-mono text-center tracking-widest rounded-sm max-w-[150px] mt-2"
                              data-testid="2fa-disable-code-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="disable-password">Account Password</Label>
                            <Input
                              id="disable-password"
                              type="password"
                              placeholder="Enter your password"
                              value={twoFAPassword}
                              onChange={(e) => setTwoFAPassword(e.target.value)}
                              className="rounded-sm max-w-[300px] mt-2"
                              data-testid="2fa-disable-password-input"
                            />
                          </div>
                          <Button 
                            onClick={disable2FA}
                            disabled={disabling2FA || twoFACode.length !== 6 || !twoFAPassword}
                            variant="destructive"
                            className="rounded-sm"
                            data-testid="disable-2fa-btn"
                          >
                            {disabling2FA ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Disabling...
                              </>
                            ) : (
                              <>
                                <X className="w-4 h-4 mr-2" />
                                Disable 2FA
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Security Tips */}
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="font-display font-bold uppercase mb-4">Security Tips</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Use a strong, unique password for your admin account
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Enable 2FA to protect against unauthorized access
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Keep your authenticator app backup codes in a safe place
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Never share your admin credentials with anyone
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Review login activity regularly for suspicious access
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
}
